import { GENERIC_DATA_SESSION_TYPE_HINTS, GENERIC_DATASET_TYPE_HINTS, IElementType, IElementTypeHint, isIElementSection } from "@faro-lotv/ielement-types";
import { isSingleScanSection } from "@faro-lotv/project-source";

/** Sections that the Sphere Viewer names, besides the clusters: the areas and the data sessions */
const NAMED_SECTION_TYPE_HINTS = new Set([IElementTypeHint.area, IElementTypeHint.cluster, ...GENERIC_DATA_SESSION_TYPE_HINTS, ...GENERIC_DATASET_TYPE_HINTS]);

/**
 * Sections that only group others internally, e.g. "Data Sets", which the Sphere Viewer never names
 * and therefore are not a place even when they are the closest section of the markup
 */
const CONTAINER_SECTION_TYPE_HINTS = new Set([IElementTypeHint.attachments, IElementTypeHint.captureTree, IElementTypeHint.dataSets, IElementTypeHint.markups, IElementTypeHint.nodes, IElementTypeHint.rooms, IElementTypeHint.rootCluster, IElementTypeHint.slideContainer, IElementTypeHint.timeTravel, IElementTypeHint.unplacedUploads, IElementTypeHint.videoRecordings]);
/**
 * Elements that are not places: the project, groups, the parts of the markup itself, and the
 * panoramas and point clouds, named after their files, which the scene or scan they are in names
 */
const NOT_PLACE_TYPES = new Set([IElementType.projectRoot, IElementType.group, IElementType.markup, IElementType.markupPolygon, IElementType.measurePolygon, IElementType.img360, IElementType.pointCloudStream]);

/** Whether the element is a place of the markup: named, and neither a container nor part of it */
function isPlace(element) {
  return element.name.trim() !== "" && !NOT_PLACE_TYPES.has(element.type) && !CONTAINER_SECTION_TYPE_HINTS.has(element.typeHint);
}

/**
 * Returns where the markup is, from the outermost place to the closest one, as the Sphere Viewer
 * names them: the area, the data session and the scan or scene the markup is on.
 * A place named like its parent, e.g. a scan in a data session of one, is listed once.
 */
export function getMarkupLocationSections({
  ancestors,
  getSphereViewerUrl
}) {
  // The closest place is the scan or scene, rather than the panorama or point cloud in it
  const closestPlace = ancestors.find(isPlace);
  const places = ancestors.filter(element => element === closestPlace || isPlace(element) && (element.type === IElementType.cluster || isIElementSection(element) && NAMED_SECTION_TYPE_HINTS.has(element.typeHint))).reverse().map(place => ({
    id: place.id,
    name: place.name.trim(),
    sphereViewerUrl: getSphereViewerUrl(place)
  }));
  return places.filter(({
    name
  }, index) => name !== places[index + 1]?.name);
}

/**
 * Whether the section is a scan, e.g. a Focus or ELS scan, which has a point cloud unlike a 360
 * photo. The scans of a registration count too, though the library leaves them out.
 */
export function isScanSection(element) {
  return !!element && (isSingleScanSection(element) || isIElementSection(element) && element.typeHint === IElementTypeHint.registrationScan);
}